"use client";

import { PaginationControls } from "@/components/ui/pagination";
import { ListSkeleton } from "@/components/shared";
import { RankList } from "./RankList";
import { useListPagination } from "@/hooks";
import type { SortOrder } from "@/types";
import type { BaseListItem } from "@/types";
import { topSellerListSkeletonConfig } from "@/components/shared/list-skeleton/configs";

type RankItem = BaseListItem & { rank: number };

interface RankListWithPaginationProps<T extends RankItem> {
  initialItems: T[];
  initialPage: number;
  initialTotal: number;
  pageSize: number;
  initialSort: SortOrder;
  onPageChange: (
    page: number,
    pageSize: number,
    sortBy?: SortOrder | null,
  ) => Promise<{ items: T[]; total: number }>;
  primaryColor: string;
  emptyMessage?: string;
}

export function RankListWithPagination<T extends RankItem>({
  initialItems,
  initialPage,
  initialTotal,
  pageSize,
  initialSort,
  onPageChange,
  primaryColor,
  emptyMessage,
}: RankListWithPaginationProps<T>) {
  const {
    items,
    currentPage,
    currentSort,
    pageSize: currentPageSize,
    totalPages,
    isPending,
    handlePageChange,
    handleSortChange,
    handlePageSizeChange,
  } = useListPagination({
    initialItems,
    initialPage,
    initialTotal,
    pageSize,
    sortable: true,
    initialSort,
    onPageChange,
  });

  return (
    <div className="space-y-4">
      {isPending ? (
        <ListSkeleton columns={topSellerListSkeletonConfig} />
      ) : (
        <RankList
          items={items}
          sortBy={currentSort!}
          onSortChange={handleSortChange!}
          primaryColor={primaryColor}
          emptyMessage={emptyMessage}
        />
      )}

      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        pageSize={currentPageSize}
        onPageSizeChange={handlePageSizeChange!}
        disabled={isPending}
      />
    </div>
  );
}
